import '@/index.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { I18nProvider } from './i18n/I18nContext';
import MetaPixelRouterTracker from './lib/metaPixel';
import AuthCallback from './components/AuthCallback';
import AppShell from './components/AppShell';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Discover from './pages/Discover';
import MapPage from './pages/MapPage';
import Collections from './pages/Collections';
import Profile from './pages/Profile';
import Catalog3D from './pages/Catalog3D';
import Quests from './pages/Quests';
import Leaderboard from './pages/Leaderboard';
import Achievements from './pages/Achievements';
import Search from './pages/Search';
import Friends from './pages/Friends';
import Admin from './pages/Admin';
import Challenges from './pages/Challenges';
import ARPage from './pages/ARPage';
import Vault from './pages/Vault';
import Records from './pages/Records';
import Arena from './pages/Arena';
import Stats from './pages/Stats';
import Regions from './pages/Regions';
import Alchemy from './pages/Alchemy';
import Marketplace from './pages/Marketplace';
// import DeckBuilder from './pages/DeckBuilder'; // shelved with V3 (tap combat is active)
import BattleHub from './pages/battle/BattleHub';
import BattleHistory from './pages/battle/BattleHistory';
import BattleProfile from './pages/battle/BattleProfile';
import SeasonsPage from './pages/battle/SeasonsPage';
import Duels from './pages/battle/Duels';
import PublicDiscovery from './pages/PublicDiscovery';
import AdminLogin from './pages/AdminLogin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import LiveArena from './pages/LiveArena';
import BattleLiveDispatcher from './pages/BattleLiveDispatcher';
import CinematicArena from './pages/CinematicArena';
import Upgrade from './pages/Upgrade';
import Membership from './pages/Membership';
import UpgradeSuccess from './pages/UpgradeSuccess';
import UpgradeCanceled from './pages/UpgradeCanceled';
import SpeciesDetail from './pages/SpeciesDetail';
import BootstrapLogin from './pages/BootstrapLogin';
import BootstrapDashboard from './pages/BootstrapDashboard';
import Observations from './pages/Observations';
import ObservationDetail from './pages/ObservationDetail';
import BiodiversityMap from './pages/BiodiversityMap';
import VerificationQueue from './pages/VerificationQueue';
import Organizations from './pages/Organizations';
import OrganizationDashboard from './pages/OrganizationDashboard';
import WorldBosses from './pages/WorldBosses';
import WorldBossDetail from './pages/WorldBossDetail';
import PublicOrgPage from './pages/PublicOrgPage';
import AIExplorerAdmin from './pages/AIExplorerAdmin';
import GbifPublication from './pages/GbifPublication';
import GameBible from './pages/GameBible';
import ExplorersLodge from './pages/ExplorersLodge';
// import ArenaV3 from './pages/ArenaV3'; // shelved — tap combat is the active battle style
import { isAdminUser } from './lib/routing';
import { Toaster } from './components/ui/sonner';
import { Loader2 } from 'lucide-react';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!isAdminUser(user)) return <Navigate to="/dashboard" replace />;
  return children;
}

function RequireBootstrap({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );
  if (!user) return <Navigate to="/bootstrap/login" replace />;
  if (!user.is_bootstrap_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/bootstrap/login" element={<BootstrapLogin />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/d/:obsId" element={<PublicDiscovery />} />
      {/* Public citation page for GBIF publications — no auth required */}
      <Route path="/gbif/:publicationId" element={<GbifPublication />} />
      {/* Public shareable org page — verified orgs only */}
      <Route path="/orgs/public/:orgId" element={<PublicOrgPage />} />

      {/* Bootstrap Console — platform owner only */}
      <Route element={<RequireBootstrap><AppShell /></RequireBootstrap>}>
        <Route path="/bootstrap/dashboard" element={<BootstrapDashboard />} />
      </Route>

      <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/collections" element={<Collections />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/quests" element={<Quests />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/achievements" element={<Achievements />} />
        <Route path="/search" element={<Search />} />
        <Route path="/friends" element={<Friends />} />
        {/* Admin portal — RBAC-gated */}
        <Route path="/admin" element={<RequireAdmin><Admin /></RequireAdmin>} />
        <Route path="/admin/dashboard" element={<RequireAdmin><Admin /></RequireAdmin>} />
        <Route path="/challenges" element={<Challenges />} />
        <Route path="/vault" element={<Vault />} />
        <Route path="/records" element={<Records />} />
        <Route path="/arena" element={<Arena />} />
        <Route path="/stats" element={<Stats />} />
        <Route path="/regions" element={<Regions />} />
        <Route path="/alchemy" element={<Alchemy />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/bible" element={<GameBible />} />
        <Route path="/lodge" element={<ExplorersLodge />} />
        {/* Battle system */}
        <Route path="/battle" element={<BattleHub />} />
        <Route path="/battle/practice" element={<Arena />} />
        <Route path="/battle/ranked" element={<Arena />} />
        <Route path="/battle/casual" element={<Arena />} />
        <Route path="/battle/history" element={<BattleHistory />} />
        <Route path="/battle/profile" element={<BattleProfile />} />
        <Route path="/battle/seasons" element={<SeasonsPage />} />
        <Route path="/battle/duels" element={<Duels />} />
        {/* Battle Arena 3.0 — real-time tap combat */}
        <Route path="/battle/live/:battleId" element={<BattleLiveDispatcher />} />
        <Route path="/battle/live-3d/:battleId" element={<CinematicArena />} />
        <Route path="/catalog/3d" element={<Catalog3D />} />
        <Route path="/upgrade" element={<Upgrade />} />
        <Route path="/membership" element={<Membership />} />
        <Route path="/upgrade/canceled" element={<UpgradeCanceled />} />
        <Route path="/species/:speciesId" element={<SpeciesDetail />} />
        {/* WildQuest Biodiversity Network */}
        <Route path="/observations" element={<Observations />} />
        <Route path="/observations/:observationId" element={<ObservationDetail />} />
        <Route path="/biodiversity" element={<BiodiversityMap />} />
        <Route path="/verify" element={<VerificationQueue />} />
        <Route path="/organizations" element={<Organizations />} />
        <Route path="/organizations/:orgId" element={<OrganizationDashboard />} />
        <Route path="/world-bosses" element={<WorldBosses />} />
        <Route path="/world-bosses/:bossId" element={<WorldBossDetail />} />
        {/* AI Explorer Management (admin-only enforced by API) */}
        <Route path="/admin/ai-explorers" element={<AIExplorerAdmin />} />
        {/* NOTE: Arena V3 (three-lane card battle) and DeckBuilder are shelved.
            Tap combat via LiveArena is the active battle style. Files kept for
            reference; routes intentionally removed. */}
      </Route>
      <Route path="/upgrade/success" element={<ProtectedRoute><UpgradeSuccess /></ProtectedRoute>} />
      <Route path="/discover" element={<ProtectedRoute><Discover /></ProtectedRoute>} />
      <Route path="/ar" element={<ProtectedRoute><ARPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <MetaPixelRouterTracker />
            <AppRouter />
            <Toaster position="top-center" theme="dark" />
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}
